import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import {Tooltip as ReactTooltip} from 'react-tooltip';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { placesSelector } from '../../store/places/selectors';
import { skillsSelector } from '../../store/skills/selectors';
import { addSnackbar, dismissSnackbar } from '../../store/organizationList/actions'
import { generateTimesheet } from '../../helpers/time_sheet_export_burro'
import { generateWeeklyWorkTimesExport } from '../../helpers/weekly_work_times_export'

import MainLayout from '../../components/Core/MainLayout';
import CustomSelect from '../../components/Core/CustomSelect'
import Progress from '../../components/Core/Progress';
import FlatButton from '../../components/Core/FlatButton/FlatButton';
import Button from '../../components/Core/Button/Button';
import ArrowEIPIcon from '../../components/Icons/ArrowEIPIcon';
import ExcelIcon from '../../components/Icons/ExcelIcon';
import TrashIcon from '../../components/Icons/TrashIcon';
import Chevron from '../../components/Icons/Chevron';
import usePermissions from '../../components/Core/usePermissions';
import GenerationAITimeSheet from '../../components/Core/Dialog/GenerationAITimeSheet';
import FavoriteFilters from '../../components/FavotiteFilters'
import { generateResourcesFromEmployees, getCheckedEmployeeIds, setCheckedEmployees, updateAllEmployees } from '../../components/TimeOff/Calendar/utils'

import {
  scheduleSelector, JournalDataSelector
} from '../../store/settings/selectors';
import {
  getSheet,
  downloadIntegration,
  downloadAIIntegration,
  checkIntegration,
  setSheet,
} from '../../store/sheet/actions';
import {
  getSchedule, loadEmployeesAll, loadIntegrations, getSettingWorkTime, loadTimeSheet, loadLogbookJournal
} from '../../store/settings/actions';
import { generateAITimeSheet, getGeneratedTimeSheet, getGeneratedTimeSheets, removeGeneratedTimeSheet, downloadGeneratedAITimeSheet, getWeeklyWorkTimesExportData } from '../../api'

import { TimeSheetDataSelector } from '../../store/settings/selectors';
import { sheetSelector, isLoadingSelector } from '../../store/sheet/selectors';
import { IntegrationsDataSelector } from '../../store/settings/selectors';
import { getPlaces } from '../../store/places/actions';
import { getSkills } from '../../store/skills/actions';

import MonthView from './MonthView';
import PagesBlock from './PagesBlock';
import './TimeSheet.scss';

const permissionsConfig = [
  {
    name: 'cost',
    module: 'cost_earning',
  },
  {
    name: 'time_sheet_costs',
    module: 'time_sheet',
    permission: 'time_sheet_costs',
  },
  {
    name: 'integrations_module',
    module: 'integrations',
    permission: 'integrations_module_access',
  },
  {
    name: 'schedule_create_and_edit',
    module: 'schedule_shift',
    permission: 'schedule_create_and_edit',
  }, {
    name: 'use_ai_evaluation',
    module: 'use_ai_evaluation',
  }
];

const timeConvert = (num) => {
  const hours = (num / 60);
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);
  return rhours+":"+String(rminutes).padStart(2, '0');
}

const AttentionIcon = ({color, ...props}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={30}
      height={30}
      fill="none"
      {...props}
    >
      <path
        fill="#FD4646"
        d="M15 28c7.18 0 13-5.82 13-13S22.18 2 15 2 2 7.82 2 15s5.82 13 13 13Z"
        opacity={0.1}
      />
      <path
        fill="#FD4646"
        d="M15 30c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15C6.716 0 0 6.716 0 15c0 8.284 6.716 15 15 15Z"
        opacity={0.1}
      />
      <path
        fill="#FD4646"
        d="M14.132 7.52a1 1 0 0 1 1.736 0l6.277 10.98a1.001 1.001 0 0 1-.868 1.5H8.723a1 1 0 0 1-.868-1.5l6.277-10.98Z"
      />
      <path stroke="#fff" strokeLinecap="round" d="M15 12.37v2.664" />
      <path
        stroke="#fff"
        strokeLinecap="round"
        strokeWidth={1.2}
        d="M15 16.707v.009"
      />
    </svg>
  )
}

export const AttentionActiveIcon = ({color, ...props}) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={30}
      height={30}
      fill="none"
      {...props}
    >
      <path
        fill="#FD4646"
        d="M15 30c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15C6.716 0 0 6.716 0 15c0 8.284 6.716 15 15 15Z"
      />
      <path
        fill="#fff"
        d="M14.132 7.52a1 1 0 0 1 1.736 0l6.277 10.98a1.001 1.001 0 0 1-.868 1.5H8.723a1 1 0 0 1-.868-1.5l6.277-10.98Z"
      />
      <path stroke="#FD4646" strokeLinecap="round" d="M15 12.37v2.664" />
      <path
        stroke="#FD4646"
        strokeLinecap="round"
        strokeWidth={1.2}
        d="M15 16.707v.009"
      />
    </svg>
  )
}

const onPage = 20

export default () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [filter, setFilter] = useState({ employers: [], places: [], skills: [], employees: [] });
  const selectPlaces = useSelector(placesSelector);
  const selectSkills = useSelector(skillsSelector);
  const company = useSelector(state => state.company.companyInfo)
  const fromDateRef = useRef(moment());
  const [currentDate, setCurrentDate] = useState(moment());
  const resizeObserverRef = useRef();
  const { id: companyId } = useParams();
  
  const sheet = useSelector(sheetSelector);
  const isLoading = useSelector(isLoadingSelector);
  const users = useSelector(state => state.settings.employees.users);
  const aiEventGeneration = useSelector(state => state.schedule.aiEventGeneration)

  const permissions = usePermissions(permissionsConfig);
  const integrations = useSelector(IntegrationsDataSelector);
  const timesheet = useSelector(TimeSheetDataSelector);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  
  const schedule = useSelector(scheduleSelector);
  const journal = useSelector(JournalDataSelector)

  const [aiSheets, setAiSheets] = useState([])
  const [aggregatedAiResults, setAggregatedAiResults] = useState({})
  const [overtimeFilter, setOvertimeFilter] = useState(false)

  const aiModalRef = useRef(null)
  const originalSheetRef = useRef(null)
  const aiTooltipRef = useRef(null)

  const checkedPlaces = getCheckedEmployeeIds(filter.places)
  const checkedSkills = getCheckedEmployeeIds(filter.skills)
  const checkedEmployees = getCheckedEmployeeIds(filter.employees)

  const isShowOvertimeIndicator = timesheet.show_overtime_indicator

  const employeesWithOvertime = useMemo(() => {
    if (!sheet?.sheet || !timesheet.show_overtime_indicator) {
      return {
        count: 0,
        list: {}
      }
    }
    const groupedByEmployee = sheet.sheet.reduce((acc, item) => {
      if (!item.employeeId) {
        return acc
      }
      const placeId = timesheet.merge_timesheet_places ? 0 : item.original_place_id
      const key = `${item.employeeId}_${placeId}`
      return {
        ...acc,
        [key]: [...(acc[key] || []), item]
      }
    }, {})
    const employees = Object.entries(groupedByEmployee).reduce((acc, [employeeId, items]) => {
      return {
        ...acc,
        [employeeId]: items.reduce((acc1, item) => {
          const date = currentDate.set('date', item.day)
          const currentItem = {
            minutes: item.total_hours,
            date: date.format('ddd DD'),
          }
          return {
            ...acc1,
            [item.day]: acc1[item.day] ? {
              minutes: acc1[item.day].minutes + currentItem.minutes,
              date: date.format('ddd DD'),
            } : currentItem
          }
        }, {})
      }
    }, {})
    const temp = Object.entries(employees).reduce((acc, [employeeId, days]) => {
      return {
        ...acc,
        [employeeId]: Object.entries(days).reduce((acc1, [day, item]) => {
          return {
            ...acc1,
            [day]: {
              ...item,
              isOvertime: item.minutes > timesheet.overtime_indicator_limit
            }
          }
        }, {})
      }
    }, {})
    const countEmployeesWithOvertime = Object.entries(temp).reduce((acc, [employeeId, days]) => {
      const hasOvertime = Object.values(days).some(day => day.isOvertime)
      return acc + (hasOvertime ? 1 : 0)
    }, 0)
    return {
      list: temp,
      count: countEmployeesWithOvertime
    }
  }, [sheet?.sheet, timesheet.overtime_indicator_limit, timesheet.show_overtime_indicator, timesheet.merge_timesheet_places, currentDate])

  const employees = users.reduce((acc, emp) => {
    const employeesFromResource = sheet?.resources?.filter(resource => resource.employeeId === emp.id) || []
    return [
      ...acc,
      ...(employeesFromResource.length
        ? employeesFromResource
        : [{
          ...emp,
          employeeId: emp.id,
          title: `${emp.name} ${emp.surname || ''}`
       }])
    ]
  }, []).filter(employee => {
    return (
      (!checkedEmployees.length || checkedEmployees.includes(employee.employeeId)) &&
      (!checkedPlaces.length || checkedPlaces.includes(employee.place_id)) &&
      (!checkedSkills.length || checkedSkills.includes(employee.skill_id))
    )
  }).filter(item => {
    const key = `${item.id}_${item.place_id}`;
    const isOvertime = employeesWithOvertime.list[key] && Object.values(employeesWithOvertime.list[key]).some((day) => day.isOvertime);
    return !overtimeFilter || isOvertime
  })

  const employeesMap = employees.reduce((acc, employee) => ({
    ...acc,
    [employee.employeeId]: employee
  }), {})
  

  const totalPages = Math.ceil(employees.length / onPage)

  useEffect(() => {
    dispatch(getPlaces(companyId));
    dispatch(getSkills(companyId));
    dispatch(getSettingWorkTime(companyId));
    dispatch(loadIntegrations(companyId));
    dispatch(loadTimeSheet(companyId));
    dispatch(getSchedule(companyId));
    dispatch(loadLogbookJournal(companyId));
    dispatch(loadEmployeesAll(companyId, {page: 'time_sheet'}))

    return () => {
      // eslint-disable-next-line
      resizeObserverRef.current?.disconnect();
    };
  }, [dispatch, companyId]);

  useEffect(() => {
    if (aiEventGeneration) {
      dispatch(dismissSnackbar())
      dispatch(addSnackbar(aiEventGeneration.message, 'success'))
      getGeneratedTimeSheets(companyId, moment(currentDate).format('YYYY-MM')).then((res) => {
        if (Array.isArray(res?.data)) {
          setAiSheets(res.data.map(i => ({...i, active: false})))
        }
      })
      setTimeout(() => {
        dispatch(dismissSnackbar())
      }, 2000)
    }
  }, [aiEventGeneration?.uniqueId, currentDate, companyId])

  useEffect(() => {
    if (Array.isArray(users)) {
      setFilter(prev => ({
        ...prev,
        employees: generateResourcesFromEmployees(users)
      }));
    }
  }, [users])

  useEffect(() => {
    if (Array.isArray(selectPlaces)) {
      setFilter(prev => ({
        ...prev,
        places: selectPlaces.map(p => ({...p, checked: false, title: p.name, isEmployee: true})),
      }));
    }
  }, [selectPlaces]);

  useEffect(() => {
    if (Array.isArray(selectSkills)) {
      setFilter(prev => ({
        ...prev,
        skills: selectSkills.map(s => ({...s, checked: false, title: s.label, isEmployee: true})),
      }));
    }
  }, [selectSkills]);

  useEffect(() => {
    if (companyId) {
      getGeneratedTimeSheets(companyId, moment(currentDate).format('YYYY-MM')).then((res) => {
        if (Array.isArray(res?.data)) {
          setAiSheets(res.data.map(i => ({...i, active: false})))
        }
      })
    }
  }, [currentDate, companyId])

  const pageEmployeeIds = (pageNumber = page) => {
    return employees
      .map(({ id }) => id)
      .slice((pageNumber - 1) * onPage, pageNumber * onPage);
  };

  const downloadIntegrationFile = async (type) => {
    if (Number(companyId) === 147) {
      const employeesMap = employees.reduce((acc, resource) => {
        const employeeData = sheet.sheet.filter(item => item.employeeId === resource.id)
        return {
          ...acc,
          [resource.id]: {
            name: resource.title,
            external_id: resource.external_id,
            total_hours: numeral(employeeData.reduce((sum, item) => sum + item.total_hours, 0)/60).format('0.[0]')*1,
            night_hours: numeral(employeeData.reduce((sum, item) => sum + item.night_hours, 0)/60).format('0.[0]')*1,
            data: employeeData.map(item => {
              return {
                day: item.day,
                total_hours: numeral(item.total_hours/60).format('0.[0]')*1,
                night_hours: numeral(item.night_hours/60).format('0.[0]')*1,
              }
            })
          },
        }
      }, {})
      
      const temp = {
        sheetName: moment(currentDate).format('YYYY.MM'),
        date: moment(currentDate).format('YYYY-MM'),
        holidays: [...new Set(Object.values(sheet.holidays).flat().map(item => item.date))],
        data: employeesMap,
      }
      generateTimesheet(temp, `${moment(currentDate).format('YYYY-MM-DD')}_${moment().format('HH-mm-ss')}-${company.name}.xlsx`)
      return
    }
    if (Number(companyId) === 198) {
      const res = await getWeeklyWorkTimesExportData(companyId, {from_date: moment(currentDate).format('YYYY-MM-DD')})
      const records = Array.isArray(res) ? res : res?.data;
      generateWeeklyWorkTimesExport(
        records,
        `${moment(currentDate).format('YYYY-MM-DD')}_${moment().format('HH-mm-ss')}-${company.name}.xlsx`,
      );
      return
    }
    let nextFromDate = moment(currentDate);
    const data = {
      integrationType: type,
      skillsArr: filter.skills.filter(s => s.checked).map(({id}) => id),
      employeesArr: getCheckedEmployeeIds(filter.employees),
      placesArr: filter.places.filter(p => p.checked).map(({id}) => id),
    };

    setLoading(true);
    dispatch(downloadIntegration(companyId, nextFromDate.format('YYYY-MM-DD'), data)).then(({ data }) => {
      if (data) {
        askIntegrationReady(data);
      } else {
        setLoading(false);
      }
    }).catch();
  }

  const askIntegrationReady = (data) => {
    dispatch(checkIntegration(
      companyId,
      data.file,
    )).then((response) => {
      if (response.status === 'ready') {
        const link = document.createElement('a');
        link.setAttribute('download', data.file);
        link.setAttribute('target', '_blank');
        link.href = `${data.path}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setLoading(false);
      } else {
        setTimeout(() => {
          askIntegrationReady(data);
        }, 1000);
      }
    });
  }

  const loadSheetByPage = async (fromDate, props = {}) => {
    setLoading(true)
    const data = {
      skillsArr: filter.skills.filter(s => s.checked).map(({id}) => id),
      placesArr: filter.places.filter(p => p.checked).map(({id}) => id),
      employeesArr: getCheckedEmployeeIds(filter.employees),
      ...props,
    }
    await dispatch(getSheet({
      companyId,
      data,
      fromDate
    })).then(res => {
      if (Array.isArray(res?.sheet)) {
        originalSheetRef.current = res.sheet
      }
    })
    setLoading(false)
  };

  const handleGetSheet = ({ fromDate = fromDateRef.current }) => {
   let nextFromDate = moment(fromDate);
    setCurrentDate(fromDate);

 
   loadSheetByPage(nextFromDate.format('YYYY-MM-DD'));
  }

  const handleSubmitFiler = () => {
    if (page !== 1) {
      setPage(1)
    }
  }

  const onEmployeesSelectFilter = (emp) => {
    setFilter((prevState) => ({
      ...prevState,
      employees: emp,
    }));
    setPage(1)
  };

  const onPlaceSelectFilter = (place) => {
    setFilter((prevState) => ({
      ...prevState,
      places: place,
    }));
    setPage(1)
  };

  const onSkillSelectFilter = (skill) => {
    setFilter((prevState) => ({
      ...prevState,
      skills: skill,
    }));
    setPage(1)
  };

  const tooltipEmployees = (pageNumber) => {
    return pageEmployeeIds(pageNumber).map((id) => {
      const employee = employeesMap[id]
      return employee.title
    }).join('<br />');
  }

  const handleClickAIAnalyse = () => {
    aiModalRef.current.open(originalSheetRef.current)
  }

  const handleSubmitAIGeneration = (data) => {
    const post = {
      ...data,
      data: data.data.map(i => {
        const { original_place_id, ...rest } = i
        return {
          ...rest,
          place_id: rest.place_id || original_place_id,
        }
      }),
      results: data.results ? JSON.parse(data.results) : null,
      month: moment(currentDate).format('YYYY-MM'),
    }
    generateAITimeSheet(companyId, post).then((res) => {
      dispatch(addSnackbar('Request successfully sent to AI', 'success'))
      setTimeout(() => {
        dispatch(dismissSnackbar())
      }, 2000)
    })
  }

  const handleSetSheet = (id) => {
    getGeneratedTimeSheet(companyId, id).then((res) => {
      if (Array.isArray(res?.results)) {
        const data = res.results.map((i) => {
          return {
            ...i,
            ...(i.current_row ? i.current_row : null)
          }
        })
        dispatch(setSheet(data))
        setAiSheets(prev => prev.map(s => ({...s, active: s.id === id})))
        const inputData = res.data.body.input.find(i => i.role === 'user') || {}
        const [content] = inputData.content || []
        if (content?.text) {
          const json = JSON.parse(content.text)
          if (Array.isArray(json?.employees)) {
            const result = json.employees.reduce((acc, employee) => {
              const aiMinutes = data.filter(d => d.employeeId === employee.employeeId).reduce((acc, row) => {
                return acc + (row.total_hours - row.break_hours)
              }, 0)
              return {
                ...acc,
                [employee.employeeId]: {
                  month_cap_minutes: timeConvert(employee.month_cap_minutes),
                  original_month_minutes: timeConvert(employee.original_month_minutes),
                  ai_month_minutes: timeConvert(aiMinutes),
                }
              }
            }, {})
            setAggregatedAiResults(result)
          }
        }
      }
    })
  }

  const handleRemoveSheet = (id) => {
    removeGeneratedTimeSheet(companyId, id).then(() => {
      setAiSheets(prev => prev.filter(s => s.id !== id))
    })
  }

  const handleSetOriginalSheet = () => {
    dispatch(setSheet(originalSheetRef.current))
    setAiSheets(prev => prev.map(s => ({...s, active: false})))
  }

  const handleDownloadIntegrationFile = (sheet) => async (e) => {
    const data = {
      integrationType: 'excel',
      skillsArr: filter.skills.map(({id}) => id),
      employeesArr: filter.employees.map(({id}) => id),
      placesArr: filter.places.map(({id}) => id),
    };
    const res = await dispatch(downloadAIIntegration(companyId, sheet.id, data))
    askIntegrationReady(res.data);
  }

  const handleChangeActiveFavoriteFilter = (data) => {
    const updatedEmployees = data.employees ? setCheckedEmployees(updateAllEmployees(filter.employees, false), data.employees || [], true) : updateAllEmployees(filter.employees, false)
    setFilter(prev => ({
      ...prev,
      places: prev.places.map(p => ({...p, checked: data.places?.includes(p.id)})),
      skills: prev.skills.map(s => ({...s, checked: data.skills?.includes(s.id)})),
      employees: updatedEmployees,
    }))

    const filters = {
      skillsArr: filter.skills.filter(s => data.skills?.includes(s.id)).map(s => s.id),
      employeesArr: getCheckedEmployeeIds(updatedEmployees),
      placesArr: filter.places.filter(p => data.places?.includes(p.id)).map(p => p.id),
    }

    loadSheetByPage(moment(currentDate).format('YYYY-MM-DD'), filters)
  }

  return (
    <MainLayout>
      <div className='timeSheet-screen'>
        <div className='timeSheet-screen__header'>
          <CustomSelect
            placeholder={t('All places')}
            buttonLabel={t('Filter')}
            items={filter.places}
            onChange={onPlaceSelectFilter}
            width='auto'
            withSearch={true}
          />
          <CustomSelect
            placeholder={t('All skills')}
            buttonLabel={t('Filter')}
            items={filter.skills}
            onChange={onSkillSelectFilter}
            width='auto'
            withSearch={true}
          />
          <CustomSelect
            placeholder={t('All employees')}
            buttonLabel={t('Filter')}
            items={filter.employees ?? []}
            onChange={onEmployeesSelectFilter}
            width='auto'
            withSearch={true}
          />
          <FavoriteFilters
            companyId={companyId}
            onChangeActive={handleChangeActiveFavoriteFilter}
            type="time-sheet"
            active={filter.places.length && filter.skills.length && filter.employees.length}
            currentFilters={{
              places: filter.places.filter(p => p.checked).map((item) => item.id),
              skills: filter.skills.filter(s => s.checked).map((item) => item.id),
              employees: getCheckedEmployeeIds(filter.employees),
            }} />
          {
            isShowOvertimeIndicator && employeesWithOvertime.count
              ? <div
                  data-tooltip-id="employees_tooltip"
                  data-tooltip-content={`${t('Show employees with overtime')}:${employeesWithOvertime.count}`}
                  className={cn('overtime-indicator', {'active': overtimeFilter})}
                  onClick={() => setOvertimeFilter(prev => !prev)}>
                  <AttentionIcon />
                  <AttentionActiveIcon />
                </div>
              : null
          }
          <Button
            onClick={handleSubmitFiler}
            className='timeSheet-screen__buttonFilter'
            disabled={isLoading || loading || (filter.employees.length === 0 && filter.places.length === 0 && filter.skills.length === 0 && false)}
          >
            {t('Search')}
          </Button>

          { permissions.integrations_module && integrations.rivile && (
            <FlatButton onClick={() => downloadIntegrationFile('eip')} className='timeSheet-screen__buttonDownload'>
              <ArrowEIPIcon className='timeSheet-screen__buttonArrow' /> {t('.EIP')}
            </FlatButton>
          )}

          { permissions.integrations_module && integrations.excel && (
            <FlatButton onClick={() => downloadIntegrationFile('excel')} className='timeSheet-screen__buttonDownload'>
              <ArrowEIPIcon className='timeSheet-screen__buttonArrow' /> <ExcelIcon />
            </FlatButton>
          )}
          {
            permissions.use_ai_evaluation
              ? <div className="ai-button-wrapper">
                  <div className="ai-button" onClick={handleClickAIAnalyse}>{t('Analyse with AI')}</div>
                  {
                    aiSheets.length
                      ? <div
                          className="ai-sheets-expander"
                          data-tooltip-id="ai_sheets"
                        >
                          <div>{aiSheets.length}</div>
                          <Chevron />
                        </div>
                      : null
                  }
                </div>
              : null
          }
          
        </div>
        {
          (!sheet) ? (
            <></>
          ) : (
            <MonthView
              resources={(employees || []).slice((page - 1) * onPage, page * onPage)}
              holidays={sheet?.holidays}
              sheet={sheet?.sheet}
              fields={sheet?.fields}
              employeesWithOvertime={employeesWithOvertime.list}
              requestBehalves={sheet?.request_behalves || []}
              onChangeMonth={handleGetSheet}
              withCost={permissions.cost && permissions.time_sheet_costs}
              withBonus={timesheet.show_bonus && journal.additional_bonus}
              withAccumulated={timesheet.use_accumulated}
              accumulatedMonths={schedule.accumulated_months}
              mergeTimesheetPlaces={timesheet.merge_timesheet_places}
              aggregatedAiResults={aggregatedAiResults}
            />
          )
        }
        {
          (loading) && (
            <div className='timeSheet-screen__overlay-loading'>
              <Progress/>
            </div>
          )
        }

        { totalPages > 1 && sheet && (
          <PagesBlock
            page={page}
            totalPages={totalPages}
            onPageChange={(newPage) => {
              setPage(newPage)
            }}
            tooltip={tooltipEmployees}
          />
        )}
      </div>
      <ReactTooltip
        id='employees_tooltip'
        className={'schedule-screen__tooltip'}
        effect='solid'
      />
      <ReactTooltip
        ref={aiTooltipRef}
        id="ai_sheets"
        effect="solid"
        openOnClick={true}
        clickable={true}
        noArrow={true}
        className={'schedule-screen__tooltip'}
        place="bottom">
        <div>
          {
            aiSheets.map(s => {
              return (
                <div key={s.id} className={cn("ai-sheet-item", {'active': s.active})} onClick={() => handleSetSheet(s.id)}>
                  <div>
                    <div className="ai-item-name">{s.name}</div>
                    <div className="ai-item-date">{moment(s.created_at).format('YYYY-MM-DD / HH:mm')}</div>
                  </div>
                  <div className="ia-item-excel-button" onClick={handleDownloadIntegrationFile(s)}>
                    <ExcelIcon />
                  </div>
                  <div className="ai-item-remove-button" onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveSheet(s.id)
                  }}>
                    <TrashIcon />
                  </div>
                </div>
              )
            })
          }
          <div className={cn("ai-shet-item-original", {'active': !aiSheets.some(i => i.active)})} onClick={handleSetOriginalSheet}>
            Original
          </div>
        </div>
      </ReactTooltip>
      <GenerationAITimeSheet ref={aiModalRef} onSubmit={handleSubmitAIGeneration} />
    </MainLayout>
  );
};
